"use client";

import {
    getCourseEmployeeStatsApiV1AdminCoursesCourseRefEmployeeStatsGet,
    listCourseEmployeesApiV1AdminCoursesCourseRefEmployeesGet,
    getModuleQuizScoresApiV1AdminCoursesCourseRefQuizScoresModuleIdGet,
} from "@/src/api/sdk.gen";
import { apiFetch } from "@/src/lib/api-fetch";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "@/src/lib/next-compat";
import {
    ArrowLeft,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    Users,
    Search,
    AlertTriangle,
    BarChart3,
    X,
} from "lucide-react";
import { dynamic } from "@/src/lib/next-compat";
import { EmployeeDetailPanel } from "./EmployeeDetailPanel";

const LevelDistribution = dynamic(
    () => import("./LevelDistribution").then((mod) => mod.LevelDistribution),
    {
        loading: () => (
            <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200 h-48 animate-pulse" />
        ),
        ssr: false,
    }
);

interface EmployeeStats {
    courseTitle: string;
    totalEnrolled: number;
    timeframeDays: number;
    completedCount: number;
    levelFunnel: Array<{
        levelName: string;
        levelNumber: number;
        count: number;
    }>;
    quizPerformance: Array<{
        moduleId: number;
        quizName: string;
        levelName: string | null;
        levelNumber: number | null;
        attempts: number;
        avgScore: number;
    }>;
    struggledConcepts: Array<{
        concept: string;
        studentCount: number;
        students: Array<{ userId: number; count: number }>;
        modules: string[];
    }>;
    studentStruggledConcepts: Record<number, string[]>;
}

interface Employee {
    userId: number;
    clerkId: string;
    email: string | null;
    name: string;
    imageUrl: string | null;
    currentLevel: string | null;
    completedLessons: number;
    totalLessons: number;
    progressPercentage: number;
    quizAverage: number | null;
    lastActiveAt: string | null;
    enrolledAt: string;
}

interface QuizStudentScore {
    userId: number;
    name: string;
    imageUrl: string | null;
    score: number;
    passed: boolean;
    submittedAt: string;
}

type Tab = "overview" | "students";
type Timeframe = "7d" | "30d" | "60d";

export function CourseProgressClient({ courseUuid }: { courseUuid: string }) {
    const [stats, setStats] = useState<EmployeeStats | null>(null);
    const [employees, setEmployees] = useState<Employee[] | null>(null);
    const [activeTab, setActiveTab] = useState<Tab>("overview");
    const [search, setSearch] = useState("");
    const [levelFilter, setLevelFilter] = useState("all");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const STUDENTS_PER_PAGE = 10;

    // Quiz scores popover state
    const [openModuleId, setOpenModuleId] = useState<number | null>(null);
    const [quizScores, setQuizScores] = useState<QuizStudentScore[]>([]);
    const [quizScoresLoading, setQuizScoresLoading] = useState(false);
    const [quizScoreSearch, setQuizScoreSearch] = useState("");
    const [selectedConcept, setSelectedConcept] = useState<string | null>(null);
    const [conceptSearch, setConceptSearch] = useState("");
    const [timeframe, setTimeframe] = useState<Timeframe>("30d");

    const fetchJson = useCallback(async <T,>(url: string, init?: RequestInit): Promise<T> => {
        const response = await apiFetch(url, init);
        if (!response.ok) {
            throw new Error(`Request failed (${response.status}) for ${url}`);
        }
        return response.json() as Promise<T>;
    }, []);

    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Stats are refetched when timeframe changes.
    useEffect(() => {
        const controller = new AbortController();
        getCourseEmployeeStatsApiV1AdminCoursesCourseRefEmployeeStatsGet({
            path: { course_ref: courseUuid },
            query: { timeframe },
            signal: controller.signal,
        })
            .then(({ data }: { data: any }) => { if (data) setStats(data as unknown as EmployeeStats); })
            .catch((err: any) => {
                if (err instanceof Error && err.name === "AbortError") return;
                console.error("Failed to fetch course stats:", err);
            });

        return () => controller.abort();
    }, [courseUuid, timeframe]);

    // All students are fetched upfront and paginated/filtered client-side.
    useEffect(() => {
        listCourseEmployeesApiV1AdminCoursesCourseRefEmployeesGet({
            path: { course_ref: courseUuid },
        })
            .then(({ data }: { data: any }) => { if (data) setEmployees((data as any).employees || []); })
            .catch((err: any) => console.error("Failed to fetch course employees:", err));
    }, [courseUuid]);

    const formatDate = (dateString: string | null) => {
        if (!dateString) return "Never";
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        if (diffMins < 60) return `${diffMins}m ago`;
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h ago`;
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays < 7) return `${diffDays}d ago`;
        return date.toLocaleDateString();
    };

    const handleModuleQuizClick = (moduleId: number) => {
        if (openModuleId === moduleId) {
            setOpenModuleId(null);
            return;
        }
        setOpenModuleId(moduleId);
        setQuizScoreSearch("");
        setQuizScoresLoading(true);
        getModuleQuizScoresApiV1AdminCoursesCourseRefQuizScoresModuleIdGet({
            path: { course_ref: courseUuid, module_id: moduleId },
        })
            .then(({ data }: { data: any }) => setQuizScores((data as any)?.studentScores || []))
            .catch((err: any) => console.error("Failed to fetch quiz scores:", err))
            .finally(() => setQuizScoresLoading(false));
    };

    const filteredQuizScores = quizScores.filter(
        (s) => !quizScoreSearch || s.name.toLowerCase().includes(quizScoreSearch.toLowerCase())
    );

    if (!stats || !employees) {
        return (
            <div className="space-y-8 animate-pulse">
                <div className="h-8 w-1/3 bg-slate-200 rounded" />
                <div className="h-10 w-64 bg-slate-100 rounded-lg" />
                <div className="h-48 bg-slate-100 rounded-2xl" />
                <div className="h-64 bg-slate-100 rounded-2xl" />
            </div>
        );
    }

    if (!stats) {
        return <div className="text-center py-12 text-slate-500">Course not found.</div>;
    }

    const filteredEmployees = employees.filter((e) => {
        const matchesSearch =
            !search ||
            e.name.toLowerCase().includes(search.toLowerCase()) ||
            e.email?.toLowerCase().includes(search.toLowerCase());
        const matchesLevel =
            levelFilter === "all" || e.currentLevel === levelFilter;
        return matchesSearch && matchesLevel;
    });

    const totalPages = Math.max(1, Math.ceil(filteredEmployees.length / STUDENTS_PER_PAGE));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const paginatedEmployees = filteredEmployees.slice(
        (safeCurrentPage - 1) * STUDENTS_PER_PAGE,
        safeCurrentPage * STUDENTS_PER_PAGE
    );

    const levels = stats.levelFunnel.map((l) => l.levelName);

    const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
        { key: "overview", label: "Course Analytics", icon: <BarChart3 className="h-4 w-4" /> },
        { key: "students", label: "Students", icon: <Users className="h-4 w-4" /> },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="sticky top-0 z-20 -mx-4 border-b border-slate-200 bg-white px-4 py-3 shadow-sm sm:-mx-6 sm:px-6 md:-mx-8 md:px-8">
                <div className="flex items-center gap-3">
                    <Link
                        to="/admin/dashboard"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
                        aria-label="Back to dashboard"
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </Link>
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                        <BarChart3 className="h-4 w-4" />
                    </div>
                    <h1 className="text-xl md:text-2xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-600">
                        {stats.courseTitle}
                    </h1>
                </div>
                <p className="mt-1 text-sm text-slate-500 font-medium leading-tight">
                    Track student progress, quiz performance, and learning bottlenecks.
                </p>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-200">
                <nav className="flex gap-6">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex items-center gap-2 pb-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === tab.key
                                    ? "border-indigo-600 text-indigo-600"
                                    : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                            }`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* Tab Content */}
            {activeTab === "overview" && stats.totalEnrolled === 0 && (
                <div className="text-center py-16 text-slate-500">
                    <Users className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm font-medium">No students have started this course yet.</p>
                </div>
            )}

            {activeTab === "overview" && stats.totalEnrolled > 0 && (
                <div className="space-y-8">
                    <div className="flex justify-end">
                        <div className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                            <span className="text-xs font-medium text-slate-600">Struggle window</span>
                            <select
                                value={timeframe}
                                onChange={(e) => setTimeframe(e.target.value as Timeframe)}
                                className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="7d">Last week</option>
                                <option value="30d">Last month</option>
                                <option value="60d">Last 60 days</option>
                            </select>
                        </div>
                    </div>

                    {/* Level Distribution + Struggled Concepts side by side */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {stats.levelFunnel.length > 0 && (
                            <LevelDistribution data={stats.levelFunnel} totalEnrolled={stats.totalEnrolled} />
                        )}

                        {stats.struggledConcepts.length > 0 && (
                            <div className="rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
                                <div className="flex items-center gap-2 mb-4">
                                    <AlertTriangle className="h-4 w-4 text-red-500" />
                                    <h3 className="text-sm font-bold text-slate-900">
                                        Common Struggled Concepts
                                    </h3>
                                    <span className="text-xs text-slate-500">
                                        ({stats.timeframeDays}d)
                                    </span>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {stats.struggledConcepts.map((item) => (
                                        <button
                                            key={item.concept}
                                            onClick={() => { setSelectedConcept(item.concept); setConceptSearch(""); }}
                                            className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100 transition-colors cursor-pointer"
                                        >
                                            {item.concept}
                                            <span className="text-red-400">
                                                ({item.studentCount} {item.studentCount === 1 ? "student" : "students"})
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Quiz Performance Table */}
                    {stats.quizPerformance.length > 0 && (
                        <div className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200 overflow-hidden">
                            <div className="px-6 py-4 border-b border-slate-200">
                                <h3 className="text-sm font-bold text-slate-900">Quiz Performance</h3>
                            </div>
                            <div className="overflow-auto max-h-[340px]">
                                <table className="w-full">
                                    <thead className="bg-slate-50/50 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                                                Quiz
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                                                Level
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                                                Total Attempts
                                            </th>
                                            <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50">
                                                Avg Score
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200">
                                        {stats.quizPerformance.map((quiz, idx) => (
                                            <tr
                                                key={idx}
                                                onClick={() => handleModuleQuizClick(quiz.moduleId)}
                                                className={`hover:bg-slate-50/80 transition-colors cursor-pointer ${openModuleId === quiz.moduleId ? "bg-indigo-50/50" : ""}`}
                                            >
                                                <td className="px-6 py-4 text-sm font-medium text-slate-900">
                                                    {quiz.quizName}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-500">
                                                    {quiz.levelName || "—"}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-500">
                                                    {quiz.attempts}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span
                                                        className={`inline-flex items-center gap-1 text-sm font-semibold ${
                                                            quiz.avgScore >= 70
                                                                ? "text-green-600"
                                                                : quiz.avgScore >= 50
                                                                ? "text-amber-600"
                                                                : "text-red-600"
                                                        }`}
                                                    >
                                                        {quiz.avgScore}%
                                                        {quiz.avgScore < 50 && (
                                                            <AlertTriangle className="h-3.5 w-3.5" />
                                                        )}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                </div>
            )}

            {activeTab === "students" && (
                <div className="space-y-0">
                    <div className="rounded-xl bg-white shadow-sm ring-1 ring-slate-200 overflow-hidden">
                        <div className="px-6 py-4 border-b border-slate-200">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4 text-slate-500" />
                                    <h3 className="text-sm font-bold text-slate-900">Students</h3>
                                    <span className="text-xs text-slate-400">
                                        ({filteredEmployees.length})
                                    </span>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Search..."
                                            value={search}
                                            onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                                            className="pl-9 pr-3 py-1.5 text-xs rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-48"
                                        />
                                    </div>
                                    <div className="relative" ref={dropdownRef}>
                                        <button
                                            onClick={() => setDropdownOpen(!dropdownOpen)}
                                            className="flex items-center gap-1.5 text-xs rounded-lg border border-slate-200 px-3 py-1.5 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                                        >
                                            {levelFilter === "all" ? "All Levels" : levelFilter}
                                            <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                                        </button>
                                        {dropdownOpen && (
                                            <div className="absolute right-0 top-full mt-1 w-44 rounded-lg bg-white border border-slate-200 shadow-lg z-20 py-1">
                                                {[{ value: "all", label: "All Levels" }, ...levels.map((l) => ({ value: l, label: l }))].map((opt) => (
                                                    <button
                                                        key={opt.value}
                                                        onClick={() => {
                                                            setLevelFilter(opt.value);
                                                            setCurrentPage(1);
                                                            setDropdownOpen(false);
                                                        }}
                                                        className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                                                            levelFilter === opt.value
                                                                ? "text-indigo-600 font-semibold bg-indigo-50"
                                                                : "text-slate-700 hover:bg-indigo-50 hover:text-indigo-600"
                                                        }`}
                                                    >
                                                        {opt.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-50/50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                            Level
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                            Progress
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                            Last Active
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {paginatedEmployees.length === 0 ? (
                                        <tr>
                                            <td colSpan={4} className="px-6 py-12 text-center text-sm text-slate-500">
                                                No students found.
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedEmployees.map((emp) => (
                                            <tr
                                                key={emp.userId}
                                                onClick={() => setSelectedEmployee(emp)}
                                                className="hover:bg-slate-50/80 transition-colors cursor-pointer"
                                            >
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        {emp.imageUrl ? (
                                                            <img
                                                                src={emp.imageUrl}
                                                                alt=""
                                                                className="h-8 w-8 rounded-full bg-slate-100 object-cover"
                                                            />
                                                        ) : (
                                                            <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600">
                                                                {emp.name.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="text-sm font-semibold text-slate-900">
                                                                {emp.name}
                                                            </div>
                                                            <div className="text-xs text-slate-500">
                                                                {emp.email}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-slate-600">
                                                    {emp.currentLevel || "—"}
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-20 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                                            <div
                                                                className="h-full bg-indigo-500 rounded-full transition-all"
                                                                style={{ width: `${emp.progressPercentage}%` }}
                                                            />
                                                        </div>
                                                        <span className="text-xs font-medium text-slate-600">
                                                            {emp.progressPercentage}%
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-xs text-slate-500">
                                                    {formatDate(emp.lastActiveAt)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        {totalPages > 1 && (
                            <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between">
                                <span className="text-xs text-slate-500">
                                    Showing {(safeCurrentPage - 1) * STUDENTS_PER_PAGE + 1}–{Math.min(safeCurrentPage * STUDENTS_PER_PAGE, filteredEmployees.length)} of {filteredEmployees.length}
                                </span>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                        disabled={safeCurrentPage === 1}
                                        className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ChevronLeft className="h-4 w-4 text-slate-600" />
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                        <button
                                            key={page}
                                            onClick={() => setCurrentPage(page)}
                                            className={`min-w-[28px] h-7 rounded-lg text-xs font-medium transition-colors ${
                                                safeCurrentPage === page
                                                    ? "bg-indigo-600 text-white"
                                                    : "text-slate-600 hover:bg-slate-100"
                                            }`}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => setCurrentPage((p) => Math.min(totalPages, Math.min(p, totalPages) + 1))}
                                        disabled={safeCurrentPage === totalPages}
                                        className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                                    >
                                        <ChevronRight className="h-4 w-4 text-slate-600" />
                                    </button>
                                </div>
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            )}

            {/* Quiz Student Scores Modal */}
            {openModuleId !== null && createPortal(
                <>
                    <div
                        className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] transition-opacity"
                        onClick={() => setOpenModuleId(null)}
                    />
                    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg animate-in fade-in zoom-in-95 duration-200">
                            {/* Header */}
                            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-900">Student Scores</h3>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                        {stats.quizPerformance.find((q) => q.moduleId === openModuleId)?.quizName}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setOpenModuleId(null)}
                                    className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Search */}
                            <div className="px-5 py-3 border-b border-slate-100">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                    <input
                                        type="text"
                                        placeholder="Search student..."
                                        value={quizScoreSearch}
                                        onChange={(e) => setQuizScoreSearch(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            {/* Student list */}
                            <div className="max-h-80 overflow-y-auto">
                                {quizScoresLoading ? (
                                    <div className="px-5 py-4 space-y-3">
                                        {[1, 2, 3, 4].map((i) => (
                                            <div key={i} className="h-10 bg-slate-100 rounded-lg animate-pulse" />
                                        ))}
                                    </div>
                                ) : filteredQuizScores.length === 0 ? (
                                    <div className="px-5 py-10 text-center text-sm text-slate-400">
                                        {quizScoreSearch ? "No matching students" : "No attempts yet"}
                                    </div>
                                ) : (
                                    <div className="divide-y divide-slate-100">
                                        {filteredQuizScores.map((student) => (
                                            <div
                                                key={student.userId}
                                                className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
                                            >
                                                <div className="flex items-center gap-3 min-w-0">
                                                    {student.imageUrl ? (
                                                        <img
                                                            src={student.imageUrl}
                                                            alt=""
                                                            className="h-8 w-8 rounded-full bg-slate-100 object-cover flex-shrink-0"
                                                        />
                                                    ) : (
                                                        <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600 flex-shrink-0">
                                                            {student.name.charAt(0).toUpperCase()}
                                                        </div>
                                                    )}
                                                    <span className="text-sm text-slate-700 truncate">
                                                        {student.name}
                                                    </span>
                                                </div>
                                                <span
                                                    className={`text-sm font-bold flex-shrink-0 ml-3 ${
                                                        student.score >= 70
                                                            ? "text-green-600"
                                                            : student.score >= 50
                                                            ? "text-amber-600"
                                                            : "text-red-600"
                                                    }`}
                                                >
                                                    {student.score}%
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </>,
                document.body
            )}

            {/* Struggled Concept Detail Modal */}
            {selectedConcept !== null && (() => {
                const conceptData = stats.struggledConcepts.find((c) => c.concept === selectedConcept);
                if (!conceptData) return null;

                const employeeMap = new Map(employees.map((e) => [e.userId, e]));
                const conceptStudentsList = conceptData.students
                    .map((s) => {
                        const emp = employeeMap.get(s.userId);
                        return emp ? { ...emp, failCount: s.count } : null;
                    })
                    .filter(Boolean) as (Employee & { failCount: number })[];

                const filteredConceptStudents = conceptStudentsList.filter(
                    (s) => !conceptSearch || s.name.toLowerCase().includes(conceptSearch.toLowerCase())
                );

                return createPortal(
                    <>
                        <div
                            className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-[60] transition-opacity"
                            onClick={() => setSelectedConcept(null)}
                        />
                        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg animate-in fade-in zoom-in-95 duration-200">
                                {/* Header */}
                                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <AlertTriangle className="h-4 w-4 text-red-500" />
                                            <h3 className="text-sm font-bold text-slate-900">{selectedConcept}</h3>
                                        </div>
                                        <p className="text-xs text-slate-500 mt-0.5">
                                            {conceptData.studentCount} {conceptData.studentCount === 1 ? "student" : "students"} struggling
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setSelectedConcept(null)}
                                        className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                                    >
                                        <X className="h-5 w-5" />
                                    </button>
                                </div>

                                {/* Related Lessons */}
                                {conceptData.modules.length > 0 && (
                                    <div className="px-5 py-3 border-b border-slate-100">
                                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">
                                            Related Modules
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {conceptData.modules.map((moduleTitle) => (
                                                <span
                                                    key={moduleTitle}
                                                    className="inline-block rounded-md bg-slate-100 px-2.5 py-1 text-xs text-slate-600"
                                                >
                                                    {moduleTitle}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Search */}
                                <div className="px-5 py-3 border-b border-slate-100">
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                        <input
                                            type="text"
                                            placeholder="Search student..."
                                            value={conceptSearch}
                                            onChange={(e) => setConceptSearch(e.target.value)}
                                            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                {/* Student list */}
                                <div className="max-h-72 overflow-y-auto">
                                    {filteredConceptStudents.length === 0 ? (
                                        <div className="px-5 py-10 text-center text-sm text-slate-400">
                                            {conceptSearch ? "No matching students" : "No students"}
                                        </div>
                                    ) : (
                                        <div className="divide-y divide-slate-100">
                                            {filteredConceptStudents.map((student) => (
                                                <div
                                                    key={student.userId}
                                                    className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors"
                                                >
                                                    <div className="flex items-center gap-3 min-w-0">
                                                        {student.imageUrl ? (
                                                            <img
                                                                src={student.imageUrl}
                                                                alt=""
                                                                className="h-8 w-8 rounded-full bg-slate-100 object-cover flex-shrink-0"
                                                            />
                                                        ) : (
                                                            <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-600 flex-shrink-0">
                                                                {student.name.charAt(0).toUpperCase()}
                                                            </div>
                                                        )}
                                                        <div className="min-w-0">
                                                            <span className="text-sm text-slate-700 truncate block">
                                                                {student.name}
                                                            </span>
                                                            <span className="text-xs text-slate-400">
                                                                {student.currentLevel || "Unknown"}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <span className="text-xs font-medium text-red-600 flex-shrink-0 ml-3 bg-red-50 px-2 py-1 rounded-full">
                                                        {student.failCount} {student.failCount === 1 ? "fail" : "fails"}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </>,
                    document.body
                );
            })()}

            {/* Employee Detail Side Panel */}
            {selectedEmployee && (
                <EmployeeDetailPanel
                    key={selectedEmployee.userId}
                    courseUuid={courseUuid}
                    employee={selectedEmployee}
                    struggledConcepts={stats.studentStruggledConcepts[selectedEmployee.userId] || []}
                    onClose={() => setSelectedEmployee(null)}
                />
            )}
        </div>
    );
}
